import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-grid.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIGrid
 * @element ui-grid
 * @classdesc Represents a class for <code>ui-grid</code> element.
 * This element creates a CSS flex- or CSS grid-enabled grid-like layout.
 * @property {("flex" | "grid")} [layout="flex"] {@attr layout} Layout type. Only applicable on desktop.
 *  {@desc grid}
 * @property {("row" | "column")} [direction="row"] {@attr direction} Direction.
 *  {@desc column}
 * @property {string} [colwidth="25%"] {@attr colwidth} Columns width in percent. Only applicable on desktop for flex layout, as well as grid layout with direction=row.
 * @property {number} [rows="2"] {@attr rows} Rows count. Only applicable on desktop with layout=grid and direction=column.
 * @property {number} [gap="0"] {@attr gap} Gap between grid items.
 * @property {boolean} [isList] {@attr is-list} If true, set the grid and its immediate children as a semantic list.
 * @property {("left" | "center" | "right" | "space-between" | "space-around")} [halign="center"] {@attr halign} Columns horizontal alignment. Only applicable on desktop with layout=flex.
 *  {@desc left}
 *  {@desc center}
 *  {@desc right}
 *  {@desc space-between}
 *  {@desc space-around}
 * @slot {@type "ui-griditem"}
 * @example
 * <ui-grid colwidth="25%">
 * </ui-grid>
 */
@Component({ tag: 'ui-grid', styles })
export class UIGrid extends UIElement {
    /** legacy naming convention, rename if seems safe */
    static readonly layout = {
        Flex: 'flex',
        Grid: 'grid',
    } as const;

    /** legacy naming convention, rename if seems safe */
    static readonly halign = {
        Center: 'center',
        Left: 'left',
        Right: 'right',
        SpaceBetween: 'space-between',
        SpaceAround: 'space-around',
    } as const;

    /** legacy naming convention, rename if seems safe */
    static readonly direction = {
        Row: 'row',
        Column: 'column',
    } as const;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'layout',
            'rows',
            'gap',
            'direction',
            'colwidth',
            'halign',
            'is-list',
        ];
    }

    @Attribute(String) layout: string = UIGrid.layout.Flex;
    @Attribute(Number) rows: number = 2;
    @Attribute(Number) gap?: number = 0;
    @Attribute(String) direction: string = UIGrid.direction.Row;
    @Attribute(String) colwidth: string = '25%';
    @Attribute(String) halign: string = UIGrid.halign.Center;
    @Attribute({ type: Boolean, name: 'is-list' }) isList?: boolean;

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        const colwidth = parseFloat(
            this.colwidth.endsWith('%')
                ? this.colwidth.slice(0, -1)
                : this.colwidth
        );
        // Ensure colwidth is a valid number and at least 1
        const safeColwidth = Math.max(
            Number.isNaN(colwidth) ? 25 : colwidth,
            1
        );
        const colnumber = Math.floor(100 / safeColwidth);
        this.style.setProperty('--ui-grid--colwidth', safeColwidth + '%');

        this.style.setProperty('--ui-grid--colnumber', String(colnumber));

        // The CSS calc() function would not let us to substract two unitless numbers, so we supply it from JS
        this.style.setProperty(
            '--ui-grid--row-gapnumber',
            String(colnumber - 1)
        );

        this.style.setProperty('--ui-grid--gap-multiplier', String(this.gap));

        if (this.layout === 'grid' && this.direction === 'column') {
            this.style.setProperty('--ui-grid--rows', String(this.rows));
        }

        if (this.isList) {
            this.setAttribute('role', 'list');
            this.querySelectorAll('ui-griditem').forEach((item) => {
                item.setAttribute('role', 'listitem');
            });
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {}

    /**
     * @inheritDoc
     */
    override hydrate(): void {}
}
